import React, {useState} from 'react';
import {Link} from 'react-router-dom';
import {Formik, Form, Field, ErrorMessage} from 'formik';
import * as Yup from 'yup';
import AccountCard from './AccountCard';
import {controlClass, fieldError, primaryButtonClass, secondaryButtonClass, Spinner} from '../formStyles';
import {CheckIcon} from '../Icons';

const passwordSchema = Yup.object({
  currentPassword: Yup.string().required('Current password is required'),
  // Matches the Supabase project's minimum; there are no complexity rules to mirror.
  newPassword: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .notOneOf([Yup.ref('currentPassword')], 'That is already your password')
    .required('New password is required'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('newPassword')], 'Passwords do not match')
    .required('Please confirm your new password'),
});

export interface ChangePasswordValues {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
}

interface ChangePasswordCardProps {
    error: string | null;
    submitting: boolean;
    /** True just after a successful change, so the card can confirm it. */
    changed: boolean;
    onSubmit: (values: ChangePasswordValues) => Promise<void>;
}

const ChangePasswordCard: React.FC<ChangePasswordCardProps> = ({
  error,
  submitting,
  changed,
  onSubmit,
}) => {
  const [editing, setEditing] = useState(false);

  return (
    <AccountCard
      title="Password"
      description="You'll need your current password to set a new one."
      error={error}
    >
      {changed && (
        <div className="mt-4 flex items-center gap-2 rounded-lg border border-emerald-500/30
         bg-emerald-500/[0.06] px-3 py-2.5 text-sm text-emerald-200">
          <CheckIcon className="w-4 h-4 shrink-0" />
          <span>Password updated.</span>
        </div>
      )}

      {!editing && (
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <span className="text-sm text-gray-500">••••••••</span>
          <button type="button" onClick={() => setEditing(true)} className={secondaryButtonClass}>
                        Change password
          </button>
        </div>
      )}

      {editing && (
        <Formik
          initialValues={{currentPassword: '', newPassword: '', confirmPassword: ''}}
          validationSchema={passwordSchema}
          onSubmit={async (values, {setSubmitting, resetForm}) => {
            await onSubmit(values);
            setSubmitting(false);
            resetForm();
          }}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({isSubmitting}) => {
            const busy = isSubmitting || submitting;

            return (
              <Form className="mt-4 space-y-3.5">
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="currentPassword">
                                        Current password
                  </label>
                  <Field
                    id="currentPassword"
                    name="currentPassword"
                    type="password"
                    autoComplete="current-password"
                    autoFocus
                    placeholder="••••••••"
                    className={controlClass}
                  />
                  <ErrorMessage name="currentPassword">{fieldError}</ErrorMessage>
                  <p className="mt-1 text-xs text-gray-600">
                                        Forgotten it?{' '}
                    <Link
                      to="/forgot-password"
                      className="font-medium text-indigo-400 hover:text-indigo-300 transition-colors"
                    >
                                            Reset it by email
                    </Link>
                    {' '}instead.
                  </p>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="newPassword">
                                        New password <span className="font-normal text-gray-600">(min. 6 characters)</span>
                  </label>
                  <Field
                    id="newPassword"
                    name="newPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className={controlClass}
                  />
                  <ErrorMessage name="newPassword">{fieldError}</ErrorMessage>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-300" htmlFor="confirmPassword">
                                        Confirm new password
                  </label>
                  <Field
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    className={controlClass}
                  />
                  <ErrorMessage name="confirmPassword">{fieldError}</ErrorMessage>
                </div>

                <div className="flex gap-2">
                  <button type="submit" disabled={busy} className={primaryButtonClass}>
                    {busy && <Spinner />}
                    {busy ? 'Updating…' : 'Update password'}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setEditing(false)}
                    className={secondaryButtonClass}
                  >
                                        Cancel
                  </button>
                </div>
              </Form>
            );
          }}
        </Formik>
      )}
    </AccountCard>
  );
};

export default ChangePasswordCard;
